/**
 * Utility functions for periodontal chart calculations and data processing
 *
 * Rules:
 * - Each present tooth always contributes 6 sites (buccal 3 + palatal/lingual 3)
 * - Missing teeth are excluded from all calculations
 * - Deciduous teeth can replace missing permanent teeth at the same position
 */

import { PERM_UPPER_RIGHT, PERM_UPPER_LEFT, PERM_LOWER_RIGHT, PERM_LOWER_LEFT, PERMANENT_TO_DECIDUOUS } from "../../config/constants/Constants";

// ─── Core helpers ───

/**
 * Get all permanent teeth numbers
 */
export const getAllPermanentTeeth = () => {
    return [...PERM_UPPER_RIGHT, ...PERM_UPPER_LEFT, ...PERM_LOWER_RIGHT, ...PERM_LOWER_LEFT];
};

/**
 * Check if a tooth number belongs to the upper arch
 */
const isUpperTooth = (toothNum) => {
    const q = Math.floor(toothNum / 10);
    return q <= 2 || (q >= 5 && q <= 6);
};

/**
 * Get the 6 measurement site keys for a tooth based on its arch position.
 * Upper teeth: db, b, mb (buccal) + dp, p, mp (palatal)
 * Lower teeth: db, b, mb (buccal) + dl, l, ml (lingual)
 */
export const getSitesForTooth = (toothNum) => {
    return isUpperTooth(toothNum) ? ["db", "b", "mb", "dp", "p", "mp"] : ["db", "b", "mb", "dl", "l", "ml"];
};

/**
 * Resolve a permanent tooth number to its effective tooth number,
 * accounting for deciduous teeth present in place of missing permanent teeth.
 */
export const getEffectiveTooth = (teethData, permanentToothNum) => {
    const deciduousToothNum = PERMANENT_TO_DECIDUOUS[permanentToothNum];
    if (deciduousToothNum && teethData[deciduousToothNum] && !teethData[deciduousToothNum].missing && teethData[permanentToothNum]?.missing) {
        return deciduousToothNum;
    }
    return permanentToothNum;
};

/**
 * Iterate each present tooth (including deciduous replacements), calling callback with (tooth, toothNum).
 * Skips missing teeth and teeth with no data.
 */
const forEachPresentTooth = (teethData, callback) => {
    getAllPermanentTeeth().forEach((permanentToothNum) => {
        const toothNum = getEffectiveTooth(teethData, permanentToothNum);
        const tooth = teethData[toothNum];
        if (!tooth || tooth.missing) return;
        callback(tooth, toothNum);
    });
};

// ─── Statistics functions ───

/**
 * Calculate teeth counts (present teeth including implants, missing teeth)
 */
export const calculateTeethCounts = (teethData) => {
    if (!teethData) return { teethCount: 0, missingCount: 0, implantCount: 0 };

    let teethCount = 0;
    let missingCount = 0;
    let implantCount = 0;

    getAllPermanentTeeth().forEach((permanentToothNum) => {
        const toothNum = getEffectiveTooth(teethData, permanentToothNum);
        const tooth = teethData[toothNum];

        // Absent teeth_data entries count as missing so the count matches getAnatomicallyOrderedMissingTeeth.
        if (!tooth || tooth.missing) {
            missingCount++;
        } else {
            teethCount++;
            if (tooth.implant) {
                implantCount++;
            }
        }
    });

    return { teethCount, missingCount, implantCount };
};

/**
 * Permanent (or replacing) tooth numbers marked as implants, in anatomical order.
 */
export const getImplantTeeth = (teethData) => {
    if (!teethData) return [];

    const implants = [];
    getAllPermanentTeeth().forEach((permanentToothNum) => {
        const toothNum = getEffectiveTooth(teethData, permanentToothNum);
        const tooth = teethData[toothNum];
        if (tooth && !tooth.missing && tooth.implant) {
            implants.push(toothNum);
        }
    });

    return implants;
};

/**
 * Calculate mean probing depth across all present teeth
 * Only counts sites that have a recorded probing depth value
 */
export const calculateMeanProbingDepth = (teethData) => {
    if (!teethData) return 0;

    let totalDepth = 0;
    let count = 0;

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            if (tooth[loc] && tooth[loc].probing_depth !== null && tooth[loc].probing_depth !== undefined) {
                totalDepth += tooth[loc].probing_depth;
                count++;
            }
        });
    });

    return count > 0 ? Math.round((totalDepth / count) * 10) / 10 : 0;
};

/**
 * Calculate mean attachment level (probing depth + recession)
 * Only counts sites that have recorded values
 */
export const calculateMeanAttachmentLevel = (teethData) => {
    if (!teethData) return 0;

    let totalAttachment = 0;
    let count = 0;

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            if (tooth[loc]) {
                const probingDepth = tooth[loc].probing_depth || 0;
                const recession = tooth[loc].recession || 0;

                if (probingDepth > 0 || recession > 0) {
                    totalAttachment += probingDepth + recession;
                    count++;
                }
            }
        });
    });

    return count > 0 ? Math.round((totalAttachment / count) * 10) / 10 : 0;
};

/**
 * Calculate plaque statistics (count and percentage)
 * Denominator is always 6 sites per present tooth
 */
export const calculatePlaqueStats = (teethData) => {
    if (!teethData) return { plaqueCount: 0, plaqueTeethCount: 0, totalSites: 0, totalTeeth: 0, percentage: 0 };

    let plaqueCount = 0;
    let totalSites = 0;
    let totalTeeth = 0;
    const plaqueTeeth = new Set();

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        totalTeeth++;
        getSitesForTooth(toothNum).forEach((loc) => {
            totalSites++;
            if (tooth[loc]?.plaque === true) {
                plaqueCount++;
                plaqueTeeth.add(toothNum);
            }
        });
    });

    return {
        plaqueCount,
        plaqueTeethCount: plaqueTeeth.size,
        totalSites,
        totalTeeth,
        percentage: totalSites > 0 ? Math.round((plaqueCount / totalSites) * 1000) / 10 : 0
    };
};

/**
 * Calculate bleeding statistics (count and percentage)
 * Denominator is always 6 sites per present tooth
 */
export const calculateBleedingStats = (teethData) => {
    if (!teethData) return { bleedingCount: 0, bleedingTeethCount: 0, totalSites: 0, totalTeeth: 0, percentage: 0 };

    let bleedingCount = 0;
    let totalSites = 0;
    let totalTeeth = 0;
    const bleedingTeeth = new Set();

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        totalTeeth++;
        getSitesForTooth(toothNum).forEach((loc) => {
            totalSites++;
            if (tooth[loc]?.bleeding === true) {
                bleedingCount++;
                bleedingTeeth.add(toothNum);
            }
        });
    });

    return {
        bleedingCount,
        bleedingTeethCount: bleedingTeeth.size,
        totalSites,
        totalTeeth,
        percentage: totalSites > 0 ? Math.round((bleedingCount / totalSites) * 1000) / 10 : 0
    };
};

/**
 * Calculate median probing depth across all present teeth
 * Only includes sites with a recorded probing depth > 0
 */
export const calculateMedianProbingDepth = (teethData) => {
    if (!teethData) return 0;

    const depths = [];

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            if (tooth[loc] && tooth[loc].probing_depth !== null && tooth[loc].probing_depth !== undefined && tooth[loc].probing_depth > 0) {
                depths.push(tooth[loc].probing_depth);
            }
        });
    });

    if (depths.length === 0) return 0;

    depths.sort((a, b) => a - b);
    const mid = Math.floor(depths.length / 2);

    return depths.length % 2 !== 0 ? depths[mid] : Math.round(((depths[mid - 1] + depths[mid]) / 2) * 10) / 10;
};

/**
 * Get teeth with probing depths at specific thresholds
 */
export const getTeethByProbingDepth = (teethData) => {
    if (!teethData) return { depth4: [], depth5: [], depth6: [], depth7plus: [] };

    const depth4 = new Set();
    const depth5 = new Set();
    const depth6 = new Set();
    const depth7plus = new Set();

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            if (tooth[loc] && tooth[loc].probing_depth) {
                const depth = tooth[loc].probing_depth;
                if (depth === 4) depth4.add(toothNum);
                else if (depth === 5) depth5.add(toothNum);
                else if (depth === 6) depth6.add(toothNum);
                else if (depth >= 7) depth7plus.add(toothNum);
            }
        });
    });

    const sortTeeth = (teeth) => Array.from(teeth).sort((a, b) => a - b);

    return {
        depth4: sortTeeth(depth4),
        depth5: sortTeeth(depth5),
        depth6: sortTeeth(depth6),
        depth7plus: sortTeeth(depth7plus)
    };
};

/**
 * Calculate risk sites statistics (sites with probing depth >= 4mm)
 * Denominator is always 6 sites per present tooth
 */
export const calculateRiskSitesStats = (teethData) => {
    if (!teethData) return { riskCount: 0, riskTeethCount: 0, totalSites: 0, totalTeeth: 0, percentage: 0 };

    let riskCount = 0;
    let totalSites = 0;
    let totalTeeth = 0;
    const riskTeeth = new Set();

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        totalTeeth++;
        getSitesForTooth(toothNum).forEach((loc) => {
            totalSites++;
            if (tooth[loc]?.probing_depth && tooth[loc].probing_depth >= 4) {
                riskCount++;
                riskTeeth.add(toothNum);
            }
        });
    });

    return {
        riskCount,
        riskTeethCount: riskTeeth.size,
        totalSites,
        totalTeeth,
        percentage: totalSites > 0 ? Math.round((riskCount / totalSites) * 1000) / 10 : 0
    };
};

// ─── Furcation deduplication ───

/**
 * Furcation deduplication mappings.
 * For teeth where buccal and palatal/lingual buttons map to the same anatomical furcation,
 * this defines which site keys are duplicates and which is canonical.
 * During statistics, if both keys have furcation data, use the deeper (higher) value
 * under the canonical key only, to avoid double-counting.
 */
const UPPER_MOLARS = [18, 17, 16, 26, 27, 28];
const UPPER_PREMOLARS = [15, 14, 24, 25];
const LOWER_PREMOLARS = [35, 34, 44, 45];

const FURCATION_DEDUP_RULES = {};

for (const t of UPPER_MOLARS) {
    FURCATION_DEDUP_RULES[t] = [
        { keys: ["mb", "mp"], canonical: "mp" },  // mesial
        { keys: ["db", "dp"], canonical: "dp" },  // distal
    ];
}
for (const t of UPPER_PREMOLARS) {
    FURCATION_DEDUP_RULES[t] = [
        { keys: ["mb", "mp"], canonical: "mp" },
        { keys: ["db", "dp"], canonical: "dp" },
    ];
}
for (const t of LOWER_PREMOLARS) {
    FURCATION_DEDUP_RULES[t] = [
        { keys: ["mb", "ml"], canonical: "ml" },
        { keys: ["db", "dl"], canonical: "dl" },
    ];
}

/**
 * Get the effective furcation value for a tooth+site, applying deduplication.
 * If the site is part of a dedup pair, returns the deeper (higher) value from both keys.
 * Returns null for non-canonical keys (to avoid double-counting in statistics).
 * For teeth/sites without dedup rules, returns the raw value.
 */
export const getEffectiveFurcation = (tooth, toothNum, siteKey) => {
    const rules = FURCATION_DEDUP_RULES[toothNum];
    if (!rules) return tooth[siteKey]?.furcation || null;

    for (const rule of rules) {
        if (rule.keys.includes(siteKey)) {
            // This site is part of a dedup pair
            if (siteKey !== rule.canonical) return null; // Skip non-canonical to avoid double-counting
            const values = rule.keys.map((k) => tooth[k]?.furcation || 0);
            const max = Math.max(...values);
            return max > 0 ? max : null;
        }
    }
    return tooth[siteKey]?.furcation || null;
};

/**
 * Get teeth with furcation involvement by class, including site location.
 * Returns arrays of { tooth, site, furcation } objects grouped by class.
 * If a tooth has multiple furcation sites, each is listed separately.
 * If a tooth has the same site at different classes, the highest class wins.
 */
export const getTeethByFurcation = (teethData) => {
    if (!teethData) return { classI: [], classII: [], classIII: [] };

    // Collect all furcation entries: Map<"toothNum-site", { tooth, site, furcation }>
    const entries = new Map();

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            const furc = getEffectiveFurcation(tooth, toothNum, loc);
            if (furc) {
                const key = `${toothNum}-${loc}`;
                const existing = entries.get(key);
                // Keep the deeper (higher) value if duplicate
                if (!existing || furc > existing.furcation) {
                    entries.set(key, { tooth: toothNum, site: loc.toUpperCase(), furcation: furc });
                }
            }
        });
    });

    const classI = [];
    const classII = [];
    const classIII = [];

    // Sort entries by tooth number, then site
    const sorted = Array.from(entries.values()).sort((a, b) => a.tooth - b.tooth || a.site.localeCompare(b.site));

    for (const entry of sorted) {
        if (entry.furcation === 1) classI.push(entry);
        else if (entry.furcation === 2) classII.push(entry);
        else if (entry.furcation === 3) classIII.push(entry);
    }

    return { classI, classII, classIII };
};

/**
 * Get mobile teeth with their classification
 */
export const getMobileTeeth = (teethData) => {
    if (!teethData) return [];

    const mobileTeeth = [];

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        if (tooth.mobility && tooth.mobility > 0) {
            mobileTeeth.push({ toothNum, mobility: tooth.mobility });
        }
    });

    return mobileTeeth.sort((a, b) => a.toothNum - b.toothNum);
};

/**
 * Generate probing depth description based on median
 */
export const generateProbingDescription = (median, depthData) => {
    const hasRiskSites = depthData.depth4.length > 0 || depthData.depth5.length > 0 || depthData.depth6.length > 0 || depthData.depth7plus.length > 0;

    let generalisedRange;
    if (median <= 2) generalisedRange = "1-2mm";
    else if (median <= 3) generalisedRange = "2-3mm";
    else if (median <= 4) generalisedRange = "3-4mm";
    else generalisedRange = "4mm+";

    if (!hasRiskSites) {
        return `Generalised ${generalisedRange} probing depths. No sites ≥4mm.`;
    }

    const localisedParts = [];
    if (depthData.depth4.length > 0) localisedParts.push(`4mm at ${depthData.depth4.join(", ")}`);
    if (depthData.depth5.length > 0) localisedParts.push(`5mm at ${depthData.depth5.join(", ")}`);
    if (depthData.depth6.length > 0) localisedParts.push(`6mm at ${depthData.depth6.join(", ")}`);
    if (depthData.depth7plus.length > 0) localisedParts.push(`7mm+ at ${depthData.depth7plus.join(", ")}`);

    return `Generalised ${generalisedRange} probing depths with localised ${localisedParts.join("; ")}.`;
};

/**
 * Calculate all summary statistics at once
 */
export const calculateSummaryStatistics = (teethData, summaryData = null) => {
    const medianProbingDepth = calculateMedianProbingDepth(teethData);
    const teethByDepth = getTeethByProbingDepth(teethData);
    const teethByFurcation = getTeethByFurcation(teethData);
    const mobileTeeth = getMobileTeeth(teethData);

    const plaqueStats = calculatePlaqueStats(teethData);
    const bleedingStats = calculateBleedingStats(teethData);
    const riskSitesStats = calculateRiskSitesStats(teethData);
    const teethCounts = calculateTeethCounts(teethData);
    const treatmentSitesStats = calculateTreatmentSitesStats(teethData);
    const detailedProbingSites = getDetailedProbingSites(teethData);
    const measuredSitesStats = calculateMeasuredSitesStats(teethData);
    const missingTeethList = getAnatomicallyOrderedMissingTeeth(teethData);

    const calculusRecorded = summaryData?.recording?.calculus === "recorded";
    const erythemaRecorded = summaryData?.recording?.erythema === "recorded";
    const calculusStats = calculusRecorded ? calculateBooleanSiteStats(teethData, "calculus") : null;
    const erythemaStats = erythemaRecorded ? calculateBooleanSiteStats(teethData, "erythema") : null;

    return {
        // Probing depth stats
        medianProbingDepth,
        generalisedRange: getGeneralisedProbingRange(teethData),

        // Plaque stats (count and percentage)
        plaqueCount: plaqueStats.plaqueCount,
        plaqueTeethCount: plaqueStats.plaqueTeethCount,
        plaquePercentage: plaqueStats.percentage,
        plaqueLocations: generatePlaqueLocationsDescription(teethData),
        plaqueSummary: generateClinicalFindingSentence(teethData, "plaque"),
        plaqueSiteCodes: calculateBooleanSiteStats(teethData, "plaque").siteCodes,

        // Bleeding stats (count and percentage)
        bleedingCount: bleedingStats.bleedingCount,
        bleedingTeethCount: bleedingStats.bleedingTeethCount,
        bleedingPercentage: bleedingStats.percentage,

        // Risk sites stats (sites >= 4mm, count and percentage)
        riskSitesCount: riskSitesStats.riskCount,
        riskTeethCount: riskSitesStats.riskTeethCount,
        riskSitesPercentage: riskSitesStats.percentage,

        // Treatment sites stats (>=5mm + 4mm BOP)
        treatmentSitesCount: treatmentSitesStats.count,
        treatmentTeethCount: treatmentSitesStats.teethCount,

        // Total sites (for reference)
        totalSites: plaqueStats.totalSites,
        measuredSites: measuredSitesStats.measuredSites,
        availableSites: measuredSitesStats.availableSites,

        // Teeth counts
        teethCount: teethCounts.teethCount,
        missingCount: teethCounts.missingCount,
        missingTeethList,
        implantCount: teethCounts.implantCount,
        implantTeeth: getImplantTeeth(teethData),

        // Calculus and erythema
        calculusRecorded,
        erythemaRecorded,
        calculusCount: calculusStats?.count ?? 0,
        calculusSiteCodes: calculusStats?.siteCodes ?? [],
        calculusLocations: calculusRecorded
            ? calculusStats.count === 0
                ? "None detected"
                : generateBooleanLocationDescription(teethData, "calculus")
            : null,
        calculusSummary: calculusRecorded ? generateClinicalFindingSentence(teethData, "calculus") : null,
        erythemaCount: erythemaStats?.count ?? 0,
        erythemaSiteCodes: erythemaStats?.siteCodes ?? [],
        erythemaLocations: erythemaRecorded ? generateBooleanLocationDescription(teethData, "erythema") : null,
        erythemaSummary: erythemaRecorded ? generateClinicalFindingSentence(teethData, "erythema") : null,
        recessionNarrative: generateRecessionNarrative(teethData),

        // Detailed breakdowns
        teethByDepth,
        detailedProbingSites,
        teethByFurcation,
        mobileTeeth,
        probingDescription: generateProbingDescription(medianProbingDepth, teethByDepth)
    };
};

/**
 * One short line for the examinations table, so a chart can be recognised without opening it.
 * Example: "18/32 · PI 9.5% · BOP 18% · PD 2-3mm · 2 deep sites"
 */
export const buildExaminationSnapshot = (teethData, summaryData = null) => {
    if (!teethData) return "";

    const stats = calculateSummaryStatistics(teethData, summaryData);
    const parts = [];

    parts.push(`${stats.teethCount}/${stats.teethCount + stats.missingCount}`);

    parts.push(`PI ${stats.plaquePercentage}%`);
    parts.push(`BOP ${stats.bleedingPercentage}%`);
    parts.push(`PD ${stats.generalisedRange}`);

    if (stats.treatmentSitesCount > 0) {
        parts.push(`${stats.treatmentSitesCount} deep site${stats.treatmentSitesCount === 1 ? "" : "s"}`);
    }

    return parts.join(" · ");
};

/**
 * Get detailed probing sites with tooth number, site location, and BOP status for each depth threshold
 */
export const getDetailedProbingSites = (teethData) => {
    if (!teethData) return { depth4: [], depth5: [], depth6: [], depth7plus: [] };

    const depth4 = [];
    const depth5 = [];
    const depth6 = [];
    const depth7plus = [];

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            if (tooth[loc] && tooth[loc].probing_depth) {
                const depth = tooth[loc].probing_depth;
                const entry = { toothNum, site: loc.toUpperCase(), hasBOP: tooth[loc].bleeding === true };

                if (depth === 4) depth4.push(entry);
                else if (depth === 5) depth5.push(entry);
                else if (depth === 6) depth6.push(entry);
                else if (depth >= 7) depth7plus.push(entry);
            }
        });
    });

    return { depth4, depth5, depth6, depth7plus };
};

/**
 * Get the generalised (majority) probing depth range
 * Based on the mode of probing depths, not mean/median
 */
export const getGeneralisedProbingRange = (teethData) => {
    if (!teethData) return "0mm";

    const depthCounts = {};

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            if (tooth[loc] && tooth[loc].probing_depth !== null && tooth[loc].probing_depth !== undefined && tooth[loc].probing_depth > 0) {
                const d = tooth[loc].probing_depth;
                depthCounts[d] = (depthCounts[d] || 0) + 1;
            }
        });
    });

    const depths = Object.keys(depthCounts)
        .map(Number)
        .sort((a, b) => a - b);
    if (depths.length === 0) return "0mm";

    // Find the most common depth (mode)
    let modeDepth = depths[0];
    let maxCount = 0;
    depths.forEach((d) => {
        if (depthCounts[d] > maxCount) {
            maxCount = depthCounts[d];
            modeDepth = d;
        }
    });

    // Build a range around the mode: include adjacent depths that are also common (>20% of mode count)
    const threshold = maxCount * 0.2;
    const rangeDepths = depths.filter((d) => Math.abs(d - modeDepth) <= 1 && depthCounts[d] >= threshold);

    const minD = Math.min(...rangeDepths);
    const maxD = Math.max(...rangeDepths);

    return minD === maxD ? `${minD}mm` : `${minD}-${maxD}mm`;
};

export const formatSiteCode = (toothNum, siteKey) => `${toothNum}${siteKey.toUpperCase()}`;

export const getAnatomicallyOrderedMissingTeeth = (teethData) => {
    if (!teethData) return [];

    const missing = [];
    getAllPermanentTeeth().forEach((permanentNum) => {
        const effectiveNum = getEffectiveTooth(teethData, permanentNum);
        const tooth = teethData[effectiveNum];
        if (!tooth || tooth.missing) {
            missing.push(permanentNum);
        }
    });

    return missing;
};

export const calculateMeasuredSitesStats = (teethData) => {
    if (!teethData) return { measuredSites: 0, availableSites: 0 };

    let measuredSites = 0;
    let availableSites = 0;

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            availableSites++;
            if (isMeasuredProbingDepth(tooth[loc]?.probing_depth)) {
                measuredSites++;
            }
        });
    });

    return { measuredSites, availableSites };
};

export const calculateBooleanSiteStats = (teethData, field) => {
    if (!teethData) return { count: 0, totalSites: 0, siteCodes: [] };

    let count = 0;
    let totalSites = 0;
    const siteCodes = [];

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            totalSites++;
            if (tooth[loc]?.[field] === true) {
                count++;
                siteCodes.push(formatSiteCode(toothNum, loc));
            }
        });
    });

    return { count, totalSites, siteCodes };
};

const getToothGroupLabel = (toothNum) => {
    const digit = toothNum % 10;
    if ([6, 7, 8].includes(digit)) return "molars";
    if ([4, 5].includes(digit)) return "premolars";
    return "anterior teeth";
};

const getSurfaceLabel = (siteKey, upper) => {
    if (["db", "b", "mb"].includes(siteKey)) return "buccal";
    return upper ? "palatal" : "lingual";
};

const INTERPROXIMAL_SITES = new Set(["db", "mb", "dp", "mp", "dl", "ml"]);

const joinWordList = (items) => {
    if (!items?.length) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

const findingNoun = (field) => {
    if (field === "plaque") return "supragingival plaque";
    if (field === "calculus") return "supragingival calculus";
    return "gingival erythema";
};

/**
 * One-sentence description of plaque, calculus, or erythema for clinical notes.
 * A region is named only when sites cluster on the same teeth. Scattered sites stay
 * "isolated". One leftover is "another isolated site"; several are "some other isolated sites".
 */
export const generateClinicalFindingSentence = (teethData, field) => {
    if (!teethData) return "None detected";

    const sites = [];
    let totalSites = 0;
    let presentTeeth = 0;

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        presentTeeth++;
        const upper = isUpperTooth(toothNum);
        const jaw = upper ? "upper" : "lower";
        getSitesForTooth(toothNum).forEach((loc) => {
            totalSites++;
            if (tooth[loc]?.[field] !== true) return;
            sites.push({
                toothNum,
                loc,
                jaw,
                surface: getSurfaceLabel(loc, upper),
                group: getToothGroupLabel(toothNum),
                interproximal: INTERPROXIMAL_SITES.has(loc)
            });
        });
    });

    const noun = findingNoun(field);
    if (sites.length === 0) {
        return field === "plaque" ? "No supragingival plaque recorded" : "None detected";
    }

    const ratio = totalSites > 0 ? sites.length / totalSites : 0;
    if (ratio >= 0.5) {
        return `Generalised ${noun} at all sites`;
    }

    const affectedTeeth = new Set(sites.map((site) => site.toothNum));
    const interproximalCount = sites.filter((site) => site.interproximal).length;
    if (interproximalCount / sites.length >= 0.7 && (affectedTeeth.size >= 8 || ratio >= 0.15)) {
        if (field === "erythema") return "Generalised interproximal erythema";
        return `Generalised interproximal ${noun}`;
    }

    // A region phrase is earned only when sites cluster: two or more teeth of the same
    // kind on one surface, or several sites on a single tooth. Lone sites stay out of
    // that phrase and are acknowledged as isolated.
    const buckets = new Map();
    sites.forEach((site) => {
        const key = `${site.jaw}|${site.surface}|${site.group}`;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(site);
    });

    const clusters = [];
    const leftovers = [];
    buckets.forEach((bucketSites) => {
        const teeth = [...new Set(bucketSites.map((site) => site.toothNum))].sort((a, b) => a - b);
        const sample = bucketSites[0];
        if (teeth.length >= 2) {
            clusters.push({ ...sample, teeth, kind: "group" });
        } else if (bucketSites.length >= 2) {
            clusters.push({ ...sample, teeth, kind: "tooth" });
        } else {
            leftovers.push(sample);
        }
    });

    const localNoun = field === "erythema" ? "erythema" : noun;
    if (clusters.length === 0) {
        const teeth = [...new Set(sites.map((site) => site.toothNum))].sort((a, b) => a - b).join(", ");
        return `Localised ${localNoun} at isolated sites (${teeth})`;
    }

    const surfaceOrder = ["buccal", "palatal", "lingual"];
    const merged = new Map();
    clusters.forEach((cluster) => {
        const key = cluster.kind === "tooth" ? `tooth|${cluster.jaw}|${cluster.teeth.join(",")}` : `group|${cluster.jaw}|${cluster.group}|${cluster.teeth.join(",")}`;
        if (!merged.has(key)) merged.set(key, []);
        merged.get(key).push(cluster);
    });

    const groupOrder = ["molars", "premolars", "anterior teeth"];
    const phrases = [...merged.values()]
        .sort((a, b) => {
            const jawDiff = (a[0].jaw === "upper" ? 0 : 1) - (b[0].jaw === "upper" ? 0 : 1);
            if (jawDiff !== 0) return jawDiff;
            const groupDiff = groupOrder.indexOf(a[0].group) - groupOrder.indexOf(b[0].group);
            if (groupDiff !== 0) return groupDiff;
            return a[0].teeth[0] - b[0].teeth[0];
        })
        .map((groupClusters) => {
            const surfaces = surfaceOrder.filter((surface) => groupClusters.some((cluster) => cluster.surface === surface));
            const sample = groupClusters[0];
            const marginWord = surfaces.length > 1 || sample.kind === "group" ? "margins" : "margin";
            const surfaceText = joinWordList(surfaces);
            if (sample.kind === "tooth") return `the ${surfaceText} ${marginWord} of ${sample.teeth[0]}`;
            return `the ${surfaceText} ${marginWord} of ${sample.jaw} ${sample.group} (${sample.teeth.join(", ")})`;
        });

    const widespread = affectedTeeth.size / Math.max(presentTeeth, 1) >= 0.5 || ratio >= 0.25;
    const tail = leftovers.length === 0 ? "" : leftovers.length === 1 ? ", and another isolated site" : ", and some other isolated sites";
    const location = `${joinWordList(phrases)}${tail}`;

    if (field === "erythema") {
        return widespread ? `Generalised gingival erythema at ${location}` : `Localised erythema at ${location}`;
    }

    return `${widespread ? "Generalised" : "Localised"} ${noun} at ${location}`;
};

/**
 * Generate auto-description of boolean site fields (plaque, calculus, erythema)
 */
export const generateBooleanLocationDescription = (teethData, field) => {
    if (!teethData) return null;

    const buccalSites = ["db", "b", "mb"];
    const palatalSites = ["dp", "p", "mp"];
    const lingualSites = ["dl", "l", "ml"];

    const isMolar = (t) => [6, 7, 8].includes(t % 10);
    const isPremolar = (t) => [4, 5].includes(t % 10);
    const isCanine = (t) => t % 10 === 3;
    const isIncisor = (t) => [1, 2].includes(t % 10);

    const regions = {
        upperBuccal: { count: 0, total: 0 },
        upperPalatal: { count: 0, total: 0 },
        lowerBuccal: { count: 0, total: 0 },
        lowerLingual: { count: 0, total: 0 }
    };

    const toothTypeRegions = {};

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        const upper = isUpperTooth(toothNum);
        let toothType;
        if (isMolar(toothNum)) toothType = "molars";
        else if (isPremolar(toothNum)) toothType = "premolars";
        else if (isCanine(toothNum)) toothType = "canines";
        else if (isIncisor(toothNum)) toothType = "incisors";

        const jaw = upper ? "upper" : "lower";
        const nonBuccalName = upper ? "palatal" : "lingual";

        const checkSites = (sites, surfaceName) => {
            sites.forEach((loc) => {
                const regionKey = `${jaw}${surfaceName.charAt(0).toUpperCase() + surfaceName.slice(1)}`;
                if (regions[regionKey]) {
                    regions[regionKey].total++;
                    if (tooth[loc]?.[field] === true) regions[regionKey].count++;
                }

                const typeKey = `${nonBuccalName} ${toothType}`;
                const buccalTypeKey = `buccal ${toothType}`;
                const key = surfaceName === "buccal" ? `${jaw} ${buccalTypeKey}` : `${jaw} ${typeKey}`;

                if (!toothTypeRegions[key]) toothTypeRegions[key] = { count: 0, total: 0 };
                toothTypeRegions[key].total++;
                if (tooth[loc]?.[field] === true) toothTypeRegions[key].count++;
            });
        };

        checkSites(buccalSites, "buccal");
        if (upper) checkSites(palatalSites, "palatal");
        else checkSites(lingualSites, "lingual");
    });

    const totalPositive = Object.values(regions).reduce((sum, r) => sum + r.count, 0);
    if (totalPositive === 0) return field === "plaque" ? "No plaque detected" : "None detected";

    const totalSites = Object.values(regions).reduce((sum, r) => sum + r.total, 0);
    const overallPct = totalSites > 0 ? totalPositive / totalSites : 0;

    if (overallPct > 0.4) {
        if (field === "plaque") return "Generalised plaque accumulation across all sites";
        if (field === "calculus") return "Generalised calculus accumulation across all sites";
        return "Generalised gingival erythema across all sites";
    }

    const significantRegions = [];
    Object.entries(toothTypeRegions).forEach(([key, data]) => {
        if (data.total > 0 && data.count / data.total > 0.15 && data.count >= 2) {
            significantRegions.push({ key, pct: data.count / data.total, count: data.count });
        }
    });

    significantRegions.sort((a, b) => b.pct - a.pct);

    if (significantRegions.length === 0) {
        return field === "plaque" ? "Scattered plaque at isolated sites" : "Isolated sites only";
    }

    return significantRegions
        .slice(0, 3)
        .map((r) => r.key)
        .join(", ");
};

const formatMmSpan = (min, max) => (min === max ? `${min}mm` : `${min}\u2013${max}mm`);

const uniqueSortedTeeth = (sites) => [...new Set(sites.map((site) => site.toothNum))].sort((a, b) => a - b);

/**
 * Split a set of recession depths into the usual range and a short "up to" tail.
 */
const summariseRecessionAmounts = (sites) => {
    const amounts = sites.map((site) => site.recession);
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);

    if (max - min <= 1) {
        return { range: formatMmSpan(min, max), outlier: "" };
    }

    const sorted = [...amounts].sort((a, b) => a - b);
    const cut = sorted[Math.max(0, Math.floor(sorted.length * 0.7) - 1)];
    const typical = sites.filter((site) => site.recession <= cut);
    const outliers = sites.filter((site) => site.recession > cut);

    if (outliers.length === 0 || outliers.length > typical.length) {
        return { range: formatMmSpan(min, max), outlier: "" };
    }

    const typicalMin = Math.min(...typical.map((site) => site.recession));
    const typicalMax = Math.max(...typical.map((site) => site.recession));
    const outlierMin = Math.min(...outliers.map((site) => site.recession));
    const outlierMax = Math.max(...outliers.map((site) => site.recession));
    const outlierLabel =
        outlierMin === outlierMax && outlierMax === typicalMax + 1 ? formatMmSpan(Math.max(outlierMax - 1, 1), outlierMax) : formatMmSpan(outlierMin, outlierMax);
    const teeth = uniqueSortedTeeth(outliers).join(", ");

    return {
        range: formatMmSpan(typicalMin, typicalMax),
        outlier: ` (up to ${outlierLabel} at ${teeth})`
    };
};

const recessionClauseForSmallChart = (positiveSites) => {
    const bySurface = new Map();
    positiveSites.forEach((site) => {
        if (!bySurface.has(site.surface)) bySurface.set(site.surface, []);
        bySurface.get(site.surface).push(site);
    });

    const parts = ["buccal", "palatal", "lingual"]
        .filter((surface) => bySurface.has(surface))
        .map((surface) => {
            const teeth = uniqueSortedTeeth(bySurface.get(surface));
            const margin = teeth.length === 1 ? "margin" : "margins";
            return `the ${surface} ${margin} of ${joinWordList(teeth.map(String))}`;
        });

    const { range } = summariseRecessionAmounts(positiveSites);
    return `Localised ${range} recession at ${joinWordList(parts)}.`;
};

export const generateRecessionNarrative = (teethData) => {
    if (!teethData) return "No gingival recession recorded.";

    const positiveSites = [];
    const overgrowthSites = [];

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        getSitesForTooth(toothNum).forEach((loc) => {
            const recession = tooth[loc]?.recession;
            if (typeof recession !== "number" || recession === 0) return;

            const entry = {
                toothNum,
                loc,
                recession,
                arch: isUpperTooth(toothNum) ? "upper" : "lower",
                surface: getSurfaceLabel(loc, isUpperTooth(toothNum))
            };

            if (recession < 0) overgrowthSites.push(entry);
            else positiveSites.push(entry);
        });
    });

    const sentences = [];

    if (positiveSites.length > 0) {
        const grouped = new Map();
        positiveSites.forEach((site) => {
            const key = `${site.arch}|${site.surface}`;
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key).push(site);
        });

        const anyBroad = [...grouped.values()].some((sites) => uniqueSortedTeeth(sites).length >= 5);

        if (!anyBroad) {
            sentences.push(recessionClauseForSmallChart(positiveSites));
        } else {
            const clauses = [];
            ["upper", "lower"].forEach((arch) => {
                const surfaces = ["buccal", "palatal", "lingual"]
                    .map((surface) => {
                        const sites = grouped.get(`${arch}|${surface}`);
                        return sites ? { surface, sites } : null;
                    })
                    .filter(Boolean);

                const broad = [];
                const narrow = [];
                surfaces.forEach((group) => {
                    if (uniqueSortedTeeth(group.sites).length >= 5) broad.push(group);
                    else narrow.push(group);
                });

                broad.forEach((group) => {
                    const { range, outlier } = summariseRecessionAmounts(group.sites);
                    const teeth = uniqueSortedTeeth(group.sites).join(", ");
                    const outlierNote = outlier.replace(/^\s*\(/, "").replace(/\)$/, "");
                    clauses.push(
                        outlierNote
                            ? `Generalised ${range} ${arch} ${group.surface} recession (${teeth}; ${outlierNote})`
                            : `Generalised ${range} ${arch} ${group.surface} recession (${teeth})`
                    );
                });

                if (narrow.length > 0) {
                    const sites = narrow.flatMap((group) => group.sites);
                    const { range, outlier } = summariseRecessionAmounts(sites);
                    const surfaceBits = narrow.map((group) => `${group.surface} ${uniqueSortedTeeth(group.sites).join(", ")}`);
                    clauses.push(`localised ${range} ${arch} recession, ${joinWordList(surfaceBits)}${outlier}`);
                }
            });

            const sentence = clauses.join(" and ");
            sentences.push(`${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`);
        }
    }

    overgrowthSites.forEach((site) => {
        const amount = Math.abs(site.recession);
        sentences.push(`Localised ${amount}mm gingival overgrowth at the ${site.surface} margin of ${site.toothNum}.`);
    });

    return sentences.join(" ") || "No gingival recession recorded.";
};

/**
 * Generate auto-description of plaque locations from data
 */
export const generatePlaqueLocationsDescription = (teethData) => generateBooleanLocationDescription(teethData, "plaque");

export const isMeasuredProbingDepth = (pd) => typeof pd === "number" && pd > 0;

export const getTreatmentRisk = (site) => {
    if (!site) return null;
    const pd = site.probing_depth;
    if (!isMeasuredProbingDepth(pd)) return null;
    const bop = site.bleeding === true;
    return pd >= 5 || (pd === 4 && bop);
};

export const getClinicalAttachmentLevel = (site) => {
    if (!site || !isMeasuredProbingDepth(site.probing_depth)) return null;
    const rec = typeof site.recession === "number" ? site.recession : 0;
    return site.probing_depth + rec;
};

/**
 * Calculate treatment-priority sites: (PD >=5mm) + (PD 4mm with BOP)
 * Denominator is always 6 sites per present tooth
 */
export const calculateTreatmentSitesStats = (teethData) => {
    if (!teethData) return { count: 0, teethCount: 0, totalSites: 0, totalTeeth: 0, percentage: 0 };

    let count = 0;
    let totalSites = 0;
    let totalTeeth = 0;
    const affectedTeeth = new Set();

    forEachPresentTooth(teethData, (tooth, toothNum) => {
        totalTeeth++;
        getSitesForTooth(toothNum).forEach((loc) => {
            totalSites++;
            if (getTreatmentRisk(tooth[loc]) === true) {
                count++;
                affectedTeeth.add(toothNum);
            }
        });
    });

    return {
        count,
        teethCount: affectedTeeth.size,
        totalSites,
        totalTeeth,
        percentage: totalSites > 0 ? Math.round((count / totalSites) * 1000) / 10 : 0
    };
};

/**
 * Get chart data for a specific arch (upper or lower)
 */
export const getArchData = (teethData, arch) => {
    if (!teethData) return {};

    const teethNumbers = arch === "upper" ? [...PERM_UPPER_RIGHT, ...PERM_UPPER_LEFT] : [...PERM_LOWER_RIGHT, ...PERM_LOWER_LEFT];

    const archData = {};
    teethNumbers.forEach((toothNum) => {
        if (teethData[toothNum]) {
            archData[toothNum] = teethData[toothNum];
        }
    });

    return archData;
};

/**
 * Compare two examinations and return structured comparison data.
 * Parameters: (followupTeethData, baselineTeethData) — current exam first, previous exam second.
 */
export const compareExaminations = (followupTeethData, baselineTeethData) => {
    const RULES_VERSION = "perio-comparison-2026-09";

    const result = {
        meta: { rulesVersion: RULES_VERSION },
        sites: [],
        teeth: { lost: [], gained: [], implantChanges: [] },
        totals: {
            improved: 0,
            deteriorated: 0,
            persistent: 0,
            worsenedWithinRisk: 0,
            stable: 0,
            indeterminate: 0,
            newRisk: 0
        },
        probingDepth: {
            improved: [],
            deteriorated: []
        },
        bleeding: {
            improved: [],
            deteriorated: [],
            remaining: []
        },
        siteMap: new Map()
    };

    if (!followupTeethData || !baselineTeethData) return result;

    const getMeasuredPD = (site) => {
        const pd = site?.probing_depth;
        return isMeasuredProbingDepth(pd) ? pd : null;
    };

    const getMeasuredRecession = (site) => (typeof site?.recession === "number" ? site.recession : 0);

    getAllPermanentTeeth().forEach((position) => {
        const baselineToothNum = getEffectiveTooth(baselineTeethData, position);
        const followupToothNum = getEffectiveTooth(followupTeethData, position);

        const baselineTooth = baselineTeethData[baselineToothNum];
        const followupTooth = followupTeethData[followupToothNum];

        const baselinePresent = baselineTooth && !baselineTooth.missing;
        const followupPresent = followupTooth && !followupTooth.missing;

        if (baselinePresent && !followupPresent) {
            result.teeth.lost.push(position);
            return;
        }

        if (!baselinePresent && followupPresent) {
            result.teeth.gained.push(position);
            return;
        }

        if (!baselinePresent && !followupPresent) {
            return;
        }

        getSitesForTooth(position).forEach((loc) => {
            const baselineSite = baselineTooth[loc];
            const followupSite = followupTooth[loc];
            if (!baselineSite || !followupSite) return;

            const label = `${position}${loc.toUpperCase()}`;
            const mapKey = `${position}-${loc}`;

            const baselinePD = getMeasuredPD(baselineSite);
            const followupPD = getMeasuredPD(followupSite);
            const baselineRisk = getTreatmentRisk(baselineSite);
            const followupRisk = getTreatmentRisk(followupSite);

            let riskState;
            if (baselineRisk === null || followupRisk === null) {
                riskState = "indeterminate";
            } else if (baselineRisk && !followupRisk) {
                riskState = "improved";
            } else if (!baselineRisk && followupRisk) {
                riskState = "deteriorated";
            } else if (baselineRisk && followupRisk) {
                riskState = "persistent";
            } else {
                riskState = "stable";
            }

            const pdDelta = baselinePD !== null && followupPD !== null ? followupPD - baselinePD : null;
            const recessionDelta =
                baselinePD !== null && followupPD !== null ? getMeasuredRecession(followupSite) - getMeasuredRecession(baselineSite) : null;

            const baselineCAL = getClinicalAttachmentLevel(baselineSite);
            const followupCAL = getClinicalAttachmentLevel(followupSite);
            const attachmentLevelDelta =
                baselineCAL !== null && followupCAL !== null ? followupCAL - baselineCAL : null;

            let magnitudeState;
            if (pdDelta === null) {
                magnitudeState = "indeterminate";
            } else if (pdDelta > 0) {
                magnitudeState = "deepened";
            } else if (pdDelta < 0) {
                magnitudeState = "reduced";
            } else {
                magnitudeState = "unchanged";
            }

            const worsenedWithinRisk = riskState === "persistent" && pdDelta !== null && pdDelta > 0;

            let bopState = null;
            if (baselinePD !== null && followupPD !== null) {
                const baselineBOP = baselineSite.bleeding === true;
                const followupBOP = followupSite.bleeding === true;
                if (baselineBOP && !followupBOP) {
                    bopState = "improved";
                    result.bleeding.improved.push(label);
                    result.siteMap.set(mapKey, (result.siteMap.get(mapKey) || "") + ",bop-improved");
                } else if (!baselineBOP && followupBOP) {
                    bopState = "deteriorated";
                    result.bleeding.deteriorated.push(label);
                    result.siteMap.set(mapKey, (result.siteMap.get(mapKey) || "") + ",bop-deteriorated");
                } else if (baselineBOP && followupBOP) {
                    bopState = "persistent";
                    result.bleeding.remaining.push(label);
                    result.siteMap.set(mapKey, (result.siteMap.get(mapKey) || "") + ",bop-remaining");
                } else {
                    bopState = "stable";
                }
            }

            result.sites.push({
                position,
                baselineTooth: baselineToothNum,
                followupTooth: followupToothNum,
                site: loc,
                label,
                baseline: {
                    pd: baselinePD,
                    bop: baselineSite.bleeding === true,
                    plaque: baselineSite.plaque === true,
                    recession: baselineSite.recession,
                    furcation: baselineSite.furcation
                },
                followup: {
                    pd: followupPD,
                    bop: followupSite.bleeding === true,
                    plaque: followupSite.plaque === true,
                    recession: followupSite.recession,
                    furcation: followupSite.furcation
                },
                riskState,
                pdDelta,
                recessionDelta,
                attachmentLevelDelta,
                magnitudeState,
                bopState,
                isNewRisk: false,
                historyState: null
            });

            result.totals[riskState]++;
            if (worsenedWithinRisk) {
                result.totals.worsenedWithinRisk++;
            }

            // The probing-depth token is prepended because consumers match it with startsWith,
            // while the already-written bleeding token is matched with includes.
            if (riskState === "improved") {
                result.probingDepth.improved.push({ site: label, previous: baselinePD, current: followupPD });
                result.siteMap.set(mapKey, "pd-improved" + (result.siteMap.get(mapKey) || ""));
            } else if (riskState === "deteriorated" || worsenedWithinRisk) {
                result.probingDepth.deteriorated.push({ site: label, previous: baselinePD, current: followupPD });
                result.siteMap.set(mapKey, "pd-deteriorated" + (result.siteMap.get(mapKey) || ""));
            }
        });
    });

    return result;
};

export const formatComparisonSiteLine = (site) => {
    const baselinePd = site.baseline.pd ?? "unmeasured";
    const followupPd = site.followup.pd ?? "unmeasured";
    const baselineBop = site.baseline.bop ? "with BOP" : "without BOP";
    const followupBop = site.followup.bop ? "with BOP" : "without BOP";

    let statusLabel = site.riskState;
    if (site.riskState === "persistent" && site.pdDelta > 0) {
        statusLabel = `remained at risk and deepened ${site.pdDelta}mm`;
    } else if (site.riskState === "deteriorated") {
        statusLabel = "new risk";
    } else if (site.riskState === "improved" && site.attachmentLevelDelta > 0) {
        statusLabel = "improved risk with attachment loss caution";
    }

    return `${site.label} — previously ${baselinePd}mm ${baselineBop}, now ${followupPd}mm ${followupBop} — ${statusLabel}`;
};

export const buildComparisonNarrative = (comparison, baselineDate, followupDate) => {
    const totals = comparison.totals;
    const parts = [
        `Compared with ${baselineDate}, ${totals.deteriorated} sites deteriorated across ${new Set(comparison.sites.filter((s) => s.riskState === "deteriorated").map((s) => s.position)).size} teeth.`,
        `${totals.improved} sites improved and ${totals.persistent} sites remained at risk.`,
        totals.worsenedWithinRisk > 0 ? `${totals.worsenedWithinRisk} persistent-risk site(s) deepened.` : null,
        comparison.teeth.lost.length > 0 ? `${comparison.teeth.lost.length} tooth/teeth were present at baseline and absent at follow-up.` : null
    ].filter(Boolean);

    return parts.join(" ");
};

export const computeSiteHistoryState = (examinationsAsc, position, loc) => {
    const riskTimeline = [];

    examinationsAsc.forEach((exam) => {
        const teethData = exam.teeth_data;
        if (!teethData) return;

        const toothNum = getEffectiveTooth(teethData, position);
        const tooth = teethData[toothNum];
        if (!tooth || tooth.missing) {
            riskTimeline.push(null);
            return;
        }

        riskTimeline.push(getTreatmentRisk(tooth[loc]));
    });

    const measuredEntries = riskTimeline.filter((risk) => risk !== null);
    if (measuredEntries.length === 0) return "insufficient_data";

    const currentRisk = riskTimeline[riskTimeline.length - 1];
    const priorMeasured = riskTimeline.slice(0, -1).filter((risk) => risk !== null);

    if (currentRisk !== true) {
        if (priorMeasured.length > 0 && priorMeasured.every((risk) => risk === false)) return "never_diseased";
        if (priorMeasured.some((risk) => risk === true)) return "resolved_sustained";
        return "never_diseased";
    }

    if (priorMeasured.length === 0) return "insufficient_data";
    if (priorMeasured.every((risk) => risk === false)) return "new_risk";
    if (priorMeasured[priorMeasured.length - 1] === true) return "chronic";
    return "recurrent";
};

export const examinationsThroughFollowup = (examinationsAsc, followupId) => {
    if (!examinationsAsc?.length || followupId == null) return examinationsAsc || [];

    const followupIndex = examinationsAsc.findIndex((exam) => exam.id === followupId);
    if (followupIndex === -1) return examinationsAsc;

    return examinationsAsc.slice(0, followupIndex + 1);
};

export const annotateComparisonWithHistory = (comparison, examinationsAsc) => {
    if (!comparison?.sites?.length || !examinationsAsc?.length) return comparison;

    comparison.totals.newRisk = 0;
    comparison.sites.forEach((site) => {
        site.historyState = computeSiteHistoryState(examinationsAsc, site.position, site.site);
        site.isNewRisk = site.historyState === "new_risk";
        if (site.isNewRisk) {
            comparison.totals.newRisk += 1;
        }
    });

    return comparison;
};

export const isDefaultComparisonFilter = (site) =>
    site.riskState === "deteriorated" || site.isNewRisk || (site.riskState === "persistent" && site.pdDelta > 0);
